/**
 * 漫画API服务
 * 用于获取漫画数据
 */

import { getApiUrl, API } from './config';

/**
 * 获取漫画列表数据
 * @param {Number} limit 限制数量
 * @param {Number} page 页码
 * @returns {Promise} 返回漫画数据的Promise
 */
export const getComics = async (page = 1, limit = 10) => {
  try {
    const url = `${getApiUrl(API.COMIC)}?page=${page}&limit=${limit}`;
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.code === 1 && data.data && data.data.list) {
      return data.data.list;
    } else {
      console.error('获取漫画列表失败:', data.msg);
      return [];
    }
  } catch (error) {
    console.error('获取漫画列表出错:', error);
    return [];
  }
};

/**
 * 获取带筛选条件的漫画列表
 * @param {Object} filters 筛选条件
 * @param {Number} filters.categoryId 分类ID (可选)
 * @param {Number} filters.isFree 是否免费 (0:全部, 1:免费, 2:付费)
 * @param {Number} page 页码
 * @param {Number} limit 每页数量
 * @returns {Promise} 返回漫画数据的Promise
 */
export const getFilteredComics = async (filters = {}, page = 1, limit = 10) => {
  try {
    let url = `${getApiUrl(API.COMIC)}?page=${page}&limit=${limit}`;
    
    // 添加分类ID筛选
    if (filters.categoryId) {
      url += `&category_id=${filters.categoryId}`;
    }
    
    // 添加付费筛选
    if (filters.isFree && filters.isFree !== 0) {
      url += `&is_free=${filters.isFree === 1 ? 1 : 0}`;
    }
    
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.code === 1 && data.data && data.data.list) {
      return {
        list: data.data.list,
        total: data.data.total,
        page: data.data.page,
        pages: data.data.pages
      };
    } else {
      console.error('获取漫画列表失败:', data.msg);
      return {
        list: [],
        total: 0,
        page: 1,
        pages: 0
      };
    }
  } catch (error) {
    console.error('获取漫画列表出错:', error);
    return {
      list: [],
      total: 0,
      page: 1,
      pages: 0
    };
  }
};

/**
 * 获取漫画详情
 * @param {Number} id 漫画ID
 * @returns {Promise} 返回漫画详情数据的Promise
 */
export const getComicDetail = async (id) => {
  try {
    const url = `${getApiUrl(API.COMIC)}/detail?id=${id}`;
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.code === 1 && data.data) {
      return data.data;
    } else {
      console.error('获取漫画详情失败:', data.msg);
      return null;
    }
  } catch (error) {
    console.error('获取漫画详情出错:', error);
    return null;
  }
};

/**
 * 获取相关推荐漫画
 * @param {Number} currentId 当前漫画ID，用于排除当前漫画
 * @param {Number} count 需要获取的数量
 * @returns {Promise} 返回相关推荐漫画数据的Promise
 */
export const getRelatedComics = async (currentId, count = 6) => {
  try {
    // 获取两页数据以确保有足够的漫画
    const comics = await getComics(1, 20);
    
    if (comics.length > 0) {
      // 排除当前漫画
      const filteredComics = comics.filter(comic => comic.id !== parseInt(currentId));
      // 随机打乱数组
      const shuffled = [...filteredComics].sort(() => 0.5 - Math.random());
      // 返回指定数量的漫画
      return shuffled.slice(0, count);
    }
    return [];
  } catch (error) {
    console.error('获取相关推荐漫画出错:', error);
    return [];
  }
};

/**
 * 获取随机漫画
 * @param {Number} count 需要获取的数量
 * @returns {Promise} 返回随机漫画数据的Promise
 */
export const getRandomComics = async (count = 6) => {
  try {
    const comics = await getComics(1, 20); // 获取更多数据以便随机选择
    
    if (comics.length > 0) {
      // 随机打乱数组
      const shuffled = [...comics].sort(() => 0.5 - Math.random());
      // 返回指定数量的漫画
      return shuffled.slice(0, count);
    }
    return [];
  } catch (error) {
    console.error('获取随机漫画出错:', error);
    return [];
  }
};

/**
 * 获取热推推荐漫画
 * @param {Number} count 需要获取的数量
 * @returns {Promise} 返回热推推荐漫画数据的Promise
 */
export const getHotRecommendComics = async (count = 16) => {
  try {
    // 获取两页数据以确保有足够的漫画
    const page1Comics = await getComics(1, 10);
    const page2Comics = await getComics(2, 10);
    const allComics = [...page1Comics, ...page2Comics];
    
    if (allComics.length > 0) {
      // 随机打乱数组
      const shuffled = [...allComics].sort(() => 0.5 - Math.random());
      // 返回指定数量的漫画
      return shuffled.slice(0, count);
    }
    return [];
  } catch (error) {
    console.error('获取热推推荐漫画出错:', error);
    return [];
  }
};

/**
 * 获取热门排行榜数据
 * @param {Number} count 需要获取的数量
 * @returns {Promise} 返回热门排行榜数据的Promise
 */
export const getHotRankingComics = async (count = 10) => {
  try {
    const comics = await getComics(1, 20);
    
    if (comics.length > 0) {
      // 按照浏览量排序（如果有浏览量字段）
      const sorted = [...comics].sort((a, b) => b.views - a.views);
      // 返回指定数量的漫画
      return sorted.slice(0, count);
    }
    return [];
  } catch (error) {
    console.error('获取热门排行榜数据出错:', error);
    return [];
  }
};

/**
 * 获取每周排行榜数据
 * @param {Number} count 需要获取的数量
 * @returns {Promise} 返回每周排行榜数据的Promise
 */
export const getWeeklyRankingComics = async (count = 10) => {
  try {
    // 获取两页数据以确保有足够的漫画
    const page1Comics = await getComics(1, 10);
    const page2Comics = await getComics(2, 10);
    const allComics = [...page1Comics, ...page2Comics];
    
    if (allComics.length > 0) {
      // 按照更新时间排序（从新到旧）
      const sorted = [...allComics].sort((a, b) => b.updatetime - a.updatetime);
      // 返回指定数量的漫画
      return sorted.slice(0, count);
    }
    return [];
  } catch (error) {
    console.error('获取每周排行榜数据出错:', error);
    return [];
  }
};

/**
 * 搜索漫画
 * @param {String} keyword 搜索关键词
 * @param {Number} page 页码
 * @param {Number} limit 每页数量
 * @returns {Promise} 返回搜索结果的Promise
 */
export const searchComics = async (keyword, page = 1, limit = 10) => {
  try {
    if (!keyword || keyword.trim() === '') {
      return {
        list: [],
        total: 0,
        page: 1,
        pages: 0,
        keyword: ''
      };
    }

    const url = `${getApiUrl(API.COMIC)}/search?keyword=${encodeURIComponent(keyword)}&page=${page}&limit=${limit}`;
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.code === 1 && data.data) {
      return {
        list: data.data.list || [],
        total: data.data.total || 0,
        page: data.data.page || 1,
        pages: data.data.pages || 0,
        keyword: data.data.keyword || keyword
      };
    } else {
      console.error('搜索漫画失败:', data.msg);
      return {
        list: [],
        total: 0,
        page: 1,
        pages: 0,
        keyword: keyword
      };
    }
  } catch (error) {
    console.error('搜索漫画出错:', error);
    return {
      list: [],
      total: 0,
      page: 1,
      pages: 0,
      keyword: keyword
    };
  }
}; 